import { useCallback, useContext, useState, useEffect } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TranslatorContext } from '@jutro/locale';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { useAuthentication, AUTH_ERRORS } from 'gw-digital-auth-react';
import AuthMessageComponent from '../AuthMessageComponent/AuthMessageComponent';
import { isEmpty, checkEmailValidity } from '../LoginUtil';

import messages from './ForgotPasswordComponent.messages';
import metadata from './ForgotPasswordComponent.metadata.json5';
import styles from '../CommonLogin.module.scss';

function ForgotPasswordComponent(props) {
    const translator = useContext(TranslatorContext);
    const [email, updateEmail] = useState('');
    const [pageError, updatePageError] = useState();
    const [pageView, setPageView] = useState();
    const { forgotPassword, authHeader } = useAuthentication();
    const { onBackToSignIn, userEmail } = props;

    useEffect(() => {
        if(!_.isUndefined(userEmail)){             
            forgotPassword(userEmail, authHeader)
            .then(() => {
                updateEmail(userEmail) 
            })
            .catch((err) => {
                switch (err.error) {
                    case AUTH_ERRORS.emailNotFound:
                        setPageView(AUTH_ERRORS.emailNotFound);
                        break;
                    default:
                        setPageView(AUTH_ERRORS.invalidAttempt);
                        break;
                }
            });         
        }
        // Stop rendering
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleForgotPassword = useCallback(
        (evt) => {
            if (evt) {
                evt.preventDefault();
            }

            if (isEmpty(email) || !checkEmailValidity(email)) {
                updatePageError(AUTH_ERRORS.fieldIncorrect);
                return Promise.resolve();
            }

            return forgotPassword(email, authHeader)
                .then(() => {
                    setPageView('success');
                })
                .catch((err) => {
                    switch (err.error) {
                        case AUTH_ERRORS.emailNotFound:
                            setPageView(AUTH_ERRORS.emailNotFound);
                            break;
                        default:
                            setPageView(AUTH_ERRORS.invalidAttempt);
                            break;
                    }
                });
        },
        [authHeader, email, forgotPassword]
    );

    const readValue = useCallback(() => email, [email]);
    const writeValue = useCallback((value) => updateEmail(value), []);

    const resolvers = {
        resolveClassNameMap: styles,
        resolveValue: readValue,
        resolveCallbackMap: {
            handleBackToSignIn: onBackToSignIn
        },
        resolveComponentMap: {
            authmessagecomponent: AuthMessageComponent
        }
    };

    const overrides = {
        '@field': {
            onValueChange: writeValue
        },
        errorMsg: {
            visible: !_.isUndefined(pageError),
            content: translator(messages[pageError])
        },
        emailFieldContainer: {
            visible: !pageView
        },
        emailForm: {
            onSubmit: handleForgotPassword
        },
        actionsWrapperSingleButton: {
            className: classNames(styles.actionsWrapperSingleButton, {
                [styles.coupleButtonStyle]: !_.isUndefined(onBackToSignIn),
                [styles.singleButtonStyle]: _.isUndefined(onBackToSignIn)
            })
        },
        sendButton: {
            trigger: handleForgotPassword,
            visible: _.isUndefined(userEmail)
        },
        linkToSignIn: {
            visible: !_.isUndefined(onBackToSignIn)  && _.isUndefined(userEmail)
        },
        invalidAttemptError: {
            visible: pageView === AUTH_ERRORS.invalidAttempt
        },
        invalidEmailError: {
            visible: pageView === AUTH_ERRORS.emailNotFound
        },
        successfulContainer: {
            visible: pageView === 'success'
        },
        username: {
            visible: _.isUndefined(userEmail),
        },
        heading:{
            content:!_.isUndefined(userEmail) ? translator(messages.forgotYourPasswordNewUser) : translator(messages.forgotYourPassword)
        },
        description:{
            content: !_.isUndefined(userEmail) ? translator(messages.forgotYourPasswordDescriptionNewUser) : translator(messages.forgotYourPasswordDescription)
        },
        closeWindowButton: {
           visible: !_.isUndefined(userEmail),
        }
       
    };

    return renderContentFromMetadata(metadata.componentContent, overrides, resolvers);
}

ForgotPasswordComponent.propTypes = {
    onBackToSignIn: PropTypes.func
};

ForgotPasswordComponent.defaultProps = {
    onBackToSignIn: undefined
};

export default ForgotPasswordComponent;
